<script>
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

export default {
  components: { SettingsSection, NextButton, TextArea },
  props: { inbox: { type: Object, default: () => ({}) } },
  data() {
    return {
      voiceEnabled: this.inbox.voice_enabled || false,
      inboundCallsEnabled: this.inbox.inbound_calls_enabled !== false,
      apiKeySid: this.inbox.api_key_sid || '',
      apiKeySecret: '',
      permissionRequestBody:
        this.inbox.provider_config?.call_permission_request_body || '',
      isUpdating: false,
      isTogglingCalling: false,
      isTogglingInbound: false,
      permissionTemplate: null,
      isCheckingPermissionTemplate: false,
    };
  },
  computed: {
    isWhatsappCloud() {
      return (
        this.inbox.channel_type === 'Channel::Whatsapp' &&
        this.inbox.provider === 'whatsapp_cloud'
      );
    },
    needsTwilioCredentials() {
      return (
        !this.isWhatsappCloud &&
        this.voiceEnabled &&
        !this.inbox.voice_configured &&
        !this.inbox.has_api_key_secret
      );
    },
    isSubmitDisabled() {
      return (
        this.needsTwilioCredentials && (!this.apiKeySid || !this.apiKeySecret)
      );
    },
  },
  watch: {
    inbox: {
      deep: true,
      handler(value) {
        this.voiceEnabled = value.voice_enabled || false;
        this.inboundCallsEnabled = value.inbound_calls_enabled !== false;
        this.apiKeySid = value.api_key_sid || '';
        this.permissionRequestBody =
          value.provider_config?.call_permission_request_body || '';
        if (this.isWhatsappCloud && this.voiceEnabled) {
          this.ensurePermissionTemplate();
        }
      },
    },
  },
  mounted() {
    if (this.isWhatsappCloud && this.voiceEnabled) {
      this.ensurePermissionTemplate();
    }
  },
  methods: {
    async refreshInbox() {
      await this.$store.dispatch('inboxes/get', this.inbox.id);
    },
    async ensurePermissionTemplate() {
      if (this.isCheckingPermissionTemplate) return;

      this.isCheckingPermissionTemplate = true;
      try {
        const response = await InboxesAPI.ensureWhatsappCallPermissionTemplate(
          this.inbox.id
        );
        this.permissionTemplate = response.data.permission_template;
      } catch (error) {
        this.permissionTemplate = {
          status: 'error',
          message: error?.response?.data?.message,
        };
      } finally {
        this.isCheckingPermissionTemplate = false;
      }
    },
    async handleInboundToggle() {
      const previousValue = !this.inboundCallsEnabled;
      this.isTogglingInbound = true;
      try {
        await InboxesAPI.setInboundCalls(
          this.inbox.id,
          this.inboundCallsEnabled
        );
        await this.refreshInbox();
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (_) {
        this.inboundCallsEnabled = previousValue;
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isTogglingInbound = false;
      }
    },
    async handleWhatsappCallingToggle() {
      const previousValue = !this.voiceEnabled;
      this.isTogglingCalling = true;
      try {
        if (this.voiceEnabled) {
          await InboxesAPI.enableWhatsappCalling(this.inbox.id);
        } else {
          await InboxesAPI.disableWhatsappCalling(this.inbox.id);
        }
        await this.refreshInbox();
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (_) {
        this.voiceEnabled = previousValue;
        useAlert(
          this.$t(
            previousValue
              ? 'INBOX_MGMT.EDIT.API.ERROR_MESSAGE'
              : 'INBOX_MGMT.WHATSAPP_CALLING.ENABLE_FAILED'
          )
        );
      } finally {
        this.isTogglingCalling = false;
      }
    },
    async updateSettings() {
      this.isUpdating = true;
      try {
        const channel = this.isWhatsappCloud
          ? {
              provider_config: {
                ...this.inbox.provider_config,
                call_permission_request_body:
                  this.permissionRequestBody.trim() || null,
              },
            }
          : {
              voice_enabled: this.voiceEnabled,
              ...(this.apiKeySid ? { api_key_sid: this.apiKeySid } : {}),
              ...(this.apiKeySecret
                ? { api_key_secret: this.apiKeySecret }
                : {}),
            };

        await this.$store.dispatch('inboxes/updateInbox', {
          id: this.inbox.id,
          formData: false,
          channel,
        });
        this.apiKeySecret = '';
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (_) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isUpdating = false;
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="
        isWhatsappCloud
          ? $t('INBOX_MGMT.WHATSAPP_CALLING.ENABLE.LABEL')
          : $t('INBOX_MGMT.VOICE_CONFIGURATION.ENABLE_VOICE.LABEL')
      "
      :sub-title="
        isWhatsappCloud
          ? $t('INBOX_MGMT.WHATSAPP_CALLING.ENABLE.DESCRIPTION')
          : $t('INBOX_MGMT.VOICE_CONFIGURATION.ENABLE_VOICE.DESCRIPTION')
      "
    >
      <input
        v-model="voiceEnabled"
        type="checkbox"
        :disabled="isTogglingCalling"
        @change="isWhatsappCloud && handleWhatsappCallingToggle()"
      />
    </SettingsSection>

    <SettingsSection
      v-if="voiceEnabled"
      :title="$t('INBOX_MGMT.VOICE_CONFIGURATION.INBOUND.LABEL')"
      :sub-title="$t('INBOX_MGMT.VOICE_CONFIGURATION.INBOUND.DESCRIPTION')"
    >
      <input
        v-model="inboundCallsEnabled"
        type="checkbox"
        :disabled="isTogglingInbound"
        @change="handleInboundToggle"
      />
    </SettingsSection>

    <SettingsSection
      v-if="needsTwilioCredentials"
      :title="$t('INBOX_MGMT.VOICE_CONFIGURATION.CREDENTIALS.TITLE')"
      :sub-title="$t('INBOX_MGMT.VOICE_CONFIGURATION.CREDENTIALS.DESCRIPTION')"
    >
      <label>
        {{ $t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SID.LABEL') }}
        <input v-model="apiKeySid" type="text" />
      </label>
      <label>
        {{ $t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SECRET.LABEL') }}
        <input v-model="apiKeySecret" type="password" />
      </label>
    </SettingsSection>

    <SettingsSection
      v-if="isWhatsappCloud && voiceEnabled"
      :title="$t('INBOX_MGMT.WHATSAPP_CALLING.PERMISSION_REQUEST_BODY.LABEL')"
      :sub-title="
        $t('INBOX_MGMT.WHATSAPP_CALLING.PERMISSION_REQUEST_BODY.HELP_TEXT')
      "
    >
      <TextArea
        v-model="permissionRequestBody"
        :placeholder="
          $t('INBOX_MGMT.WHATSAPP_CALLING.PERMISSION_REQUEST_BODY.PLACEHOLDER')
        "
        auto-height
        resize
      />
    </SettingsSection>

    <SettingsSection
      v-if="isWhatsappCloud && voiceEnabled"
      :title="$t('INBOX_MGMT.WHATSAPP_CALLING.PERMISSION_TEMPLATE.TITLE')"
      :sub-title="
        $t('INBOX_MGMT.WHATSAPP_CALLING.PERMISSION_TEMPLATE.DESCRIPTION')
      "
    >
      <p
        class="text-sm"
        :class="
          permissionTemplate?.status === 'approved'
            ? 'text-green-700 dark:text-green-300'
            : 'text-amber-700 dark:text-amber-300'
        "
      >
        <span v-if="isCheckingPermissionTemplate">
          {{ $t('INBOX_MGMT.WHATSAPP_CALLING.PERMISSION_TEMPLATE.CHECKING') }}
        </span>
        <span v-else-if="permissionTemplate?.status === 'approved'">
          {{ $t('INBOX_MGMT.WHATSAPP_CALLING.PERMISSION_TEMPLATE.APPROVED') }}
        </span>
        <span v-else-if="permissionTemplate?.status === 'pending'">
          {{ $t('INBOX_MGMT.WHATSAPP_CALLING.PERMISSION_TEMPLATE.PENDING') }}
        </span>
        <span v-else-if="permissionTemplate?.status === 'rejected'">
          {{ $t('INBOX_MGMT.WHATSAPP_CALLING.PERMISSION_TEMPLATE.REJECTED') }}
        </span>
        <span v-else-if="permissionTemplate?.status === 'error'">
          {{
            permissionTemplate.message ||
            $t('INBOX_MGMT.WHATSAPP_CALLING.PERMISSION_TEMPLATE.ERROR')
          }}
        </span>
        <span v-else>
          {{ $t('INBOX_MGMT.WHATSAPP_CALLING.PERMISSION_TEMPLATE.MISSING') }}
        </span>
      </p>
      <NextButton
        v-if="
          !isCheckingPermissionTemplate &&
          permissionTemplate?.status !== 'approved'
        "
        class="mt-3"
        :is-loading="isCheckingPermissionTemplate"
        :label="$t('INBOX_MGMT.WHATSAPP_CALLING.PERMISSION_TEMPLATE.RETRY')"
        @click="ensurePermissionTemplate"
      />
    </SettingsSection>

    <SettingsSection
      v-if="!isWhatsappCloud || voiceEnabled"
      :show-border="false"
    >
      <NextButton
        :disabled="isSubmitDisabled"
        :is-loading="isUpdating"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
        @click="updateSettings"
      />
    </SettingsSection>
  </div>
</template>
